import React, { useState, useEffect } from 'react';
import { useSocket } from '../../../hooks/useSocket';
import { useProjectState } from './useProjectState';
import SideNav from '../../../components/SideNav';
import ProjectsList from './ProjectsList';
import ProjectDetails from './ProjectDetails';
import CompletedProjectsView from './CompletedProjectsView';
import TimelineView from './TimelineView';
import MyTasksList from './MyTasksList';
import UpdateSummary from './UpdateSummary';
import NotificationBanner from './NotificationBanner';
import AnnouncementBanner from '../../../components/announcements/AnnouncementBanner';

const CITeamDashboard = () => {
  const [activeTab, setActiveTab] = useState('projects');
  const [notification, setNotification] = useState(null);

  const userId = localStorage.getItem('userId') || 'unknown';
  const { socket, isConnected } = useSocket(userId);

  const showNotification = (message, type = 'error') => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 5000);
  };

  const {
    projects,
    selectedProject,
    myTasks,
    loading,
    loadingMyTasks,
    isSubmitting,
    loadProjects,
    loadProject,
    loadMyTasks,
    createProject,
    updateProject,
    createTask,
    updateTask,
    deleteTask,
    deleteProject,
    completeMyTask,
    addNote,
    templates,
    loadTemplates,
    saveAsTemplate,
    createFromTemplate,
    deleteTemplate,
  } = useProjectState(socket, showNotification);

  useEffect(() => {
    loadProjects();
    loadTemplates();
  }, []);

  const handleSelectProject = async (project) => {
    if (project.id) {
      await loadProject(project.id);
    }
  };

  const handleTimelineProjectClick = async (project) => {
    await handleSelectProject(project);
    setActiveTab('projects');
  };

  const handleJumpToProject = async (projectId) => {
    // Switch to projects tab
    setActiveTab('projects');
    // Load the specific project
    await loadProject(projectId);
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-400">Loading projects...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-screen bg-slate-950">
      <NotificationBanner 
        notification={notification} 
        onClose={() => setNotification(null)} 
      />
      
      <div className="flex flex-1 min-h-0">
        <SideNav />
        
        <div className="flex-1 flex flex-col min-w-0">
          {/* Header with Tab Navigation */}
          <header className="bg-slate-900 border-b border-slate-800">
            <div className="px-6 py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-6">
                  <h1 className="text-2xl font-bold text-white">C.I. Team Dashboard</h1>
                  <nav className="flex space-x-1">
                    <button
                      onClick={() => setActiveTab('projects')}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        activeTab === 'projects'
                          ? 'bg-blue-600 text-white'
                          : 'text-slate-300 hover:text-white hover:bg-slate-800'
                      }`}
                    >
                      Projects
                    </button>
                    <button
                      onClick={() => setActiveTab('mytasks')}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        activeTab === 'mytasks'
                          ? 'bg-blue-600 text-white'
                          : 'text-slate-300 hover:text-white hover:bg-slate-800'
                      }`}
                    >
                      My Tasks
                    </button>
                    <button
                      onClick={() => setActiveTab('updatesummary')}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        activeTab === 'updatesummary'
                          ? 'bg-blue-600 text-white'
                          : 'text-slate-300 hover:text-white hover:bg-slate-800'
                      }`}
                    >
                      Update Summary
                    </button>
                    <button
                      onClick={() => setActiveTab('completed')}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        activeTab === 'completed'
                          ? 'bg-blue-600 text-white'
                          : 'text-slate-300 hover:text-white hover:bg-slate-800'
                      }`}
                    >
                      Completed
                    </button>
                    <button
                      onClick={() => setActiveTab('timeline')}
                      className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                        activeTab === 'timeline'
                          ? 'bg-blue-600 text-white'
                          : 'text-slate-300 hover:text-white hover:bg-slate-800'
                      }`}
                    >
                      Timeline
                    </button>
                  </nav>
                </div>
                <AnnouncementBanner />
              </div>
            </div>
          </header>

          {/* Main Content */}
          <div className="flex-1 flex overflow-hidden">
            {activeTab === 'projects' ? (
              <>
                {/* Projects List - Left Panel */}
                <ProjectsList
                  projects={projects.filter(project => !['completed', 'cancelled'].includes(project.status))}
                  selectedProject={selectedProject}
                  onSelectProject={handleSelectProject}
                  onCreateProject={createProject}
                  onUpdateProject={updateProject}
                  isSubmitting={isSubmitting}
                  socket={socket}
                  showNotification={showNotification}
                  templates={templates}
                  onLoadTemplates={loadTemplates}
                  onCreateFromTemplate={createFromTemplate}
                  onDeleteTemplate={deleteTemplate}
                />

                {/* Project Details - Right Panel */}
                <ProjectDetails
                  selectedProject={selectedProject}
                  onUpdateProject={updateProject}
                  onDeleteProject={deleteProject}
                  onCreateTask={createTask}
                  onUpdateTask={updateTask}
                  onDeleteTask={deleteTask}
                  onAddNote={addNote}
                  onSaveAsTemplate={saveAsTemplate}
                  isSubmitting={isSubmitting}
                  showNotification={showNotification}
                  socket={socket}
                />
              </>
            ) : activeTab === 'mytasks' ? (
              /* My Tasks View */
              <MyTasksList
                myTasks={myTasks}
                onCompleteTask={completeMyTask}
                onLoadMyTasks={loadMyTasks}
                onJumpToProject={handleJumpToProject}
                onAddNote={addNote}
                loading={loadingMyTasks}
              />
            ) : activeTab === 'updatesummary' ? (
              /* Update Summary View */
              <UpdateSummary />
            ) : activeTab === 'completed' ? (
              /* Completed Projects View */
              <CompletedProjectsView
                projects={projects}
                selectedProject={selectedProject}
                onSelectProject={handleSelectProject}
                onUpdateProject={updateProject}
                onDeleteProject={deleteProject}
                onCreateTask={createTask}
                onUpdateTask={updateTask}
                onDeleteTask={deleteTask}
                onAddNote={addNote}
                isSubmitting={isSubmitting}
                showNotification={showNotification}
              />
            ) : (
              /* Timeline View */
              <TimelineView
                projects={projects}
                onSelectProject={handleTimelineProjectClick}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CITeamDashboard;